import PropTypes from 'prop-types'
import { useCallback, useState } from 'react'

import { FormModal, FormModalFooter } from '@components/branded-modal'
import { AdasTextField } from '@components/wrapper-components'
import { BUTTONS_DISPLAY_LABEL, GENERIC_MESSAGES } from '../../../constants'

/**
 * A confirm pop-up: title, message and Cancel / confirm.
 *
 * CAR-4050: built on the standard pop-up (FormModal + FormModalFooter), so every confirmation looks like the
 * rest of the app - navy header, the message, outlined Cancel focused first and a navy confirm. The props are
 * unchanged, so its screens need no edits. `typeCheck` asks the user to type a word before confirming,
 * `adminCheck` asks for the system admin code, `warningMessage` shows above the message and `notifyError`
 * turns it into a notice with no buttons. `clientMayEdit`: these confirmations were never disabled for
 * read-only users, and the screens decide who may open them.
 */
export const ConfirmModal = ({
  open,
  handleClose,
  handleContinue,
  title,
  message,
  cancelLabel = BUTTONS_DISPLAY_LABEL.CANCEL,
  confirmLabel,
  typeCheck,
  warningMessage,
  adminCheck,
  adminCheckFailureMessage,
  notifyError,
  icon
}) => {
  const [code, setCode] = useState('')
  const handleModalClose = useCallback(() => {
    setCode('')
    handleClose()
  }, [handleClose])

  const handleSubmit = useCallback(() => {
    handleContinue(code)
    setCode('')
  }, [handleContinue, code])

  const confirmDisabled = typeCheck ? typeCheck !== code : Boolean(adminCheck && !code)

  return (
    <FormModal
      open={open}
      onClose={handleModalClose}
      title={title}
      icon={icon}
      width='max-w-[520px]'
      clientMayEdit
      footer={
        notifyError ? undefined : (
          <FormModalFooter
            onCancel={handleModalClose}
            cancelLabel={cancelLabel}
            onConfirm={handleSubmit}
            confirmLabel={confirmLabel}
            confirmDisabled={confirmDisabled}
          />
        )
      }
    >
      {warningMessage && <p className='mb-3 text-[13px] font-medium leading-5 text-[#92400E]'>{warningMessage}</p>}
      {typeof message === 'string' ? <p className='text-[13px] leading-5 text-[#334155]'>{message}</p> : message}
      {!notifyError && typeCheck && (
        <div className='mt-4'>
          <AdasTextField
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            name='type check'
            id='typeCheck'
            label={`Type ‘${typeCheck}’`}
            variant='outlined'
            fullWidth={true}
            InputLabelProps={{
              shrink: true
            }}
          />
        </div>
      )}
      {adminCheck && (
        <div className='mt-4'>
          <AdasTextField
            onChange={(e) => setCode(e.target.value)}
            name='Admin Code'
            id='adminCodeInput'
            label={GENERIC_MESSAGES.SYSTEM_ADMIN_CODE}
            variant='outlined'
            fullWidth={true}
            InputLabelProps={{
              shrink: true
            }}
          />
          {adminCheckFailureMessage && <p className='mx-1 mt-1 text-[12px] text-error'>{adminCheckFailureMessage}</p>}
        </div>
      )}
    </FormModal>
  )
}

ConfirmModal.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  handleContinue: PropTypes.func.isRequired,
  title: PropTypes.node.isRequired,
  message: PropTypes.oneOfType([PropTypes.string, PropTypes.object]).isRequired,
  cancelLabel: PropTypes.node,
  confirmLabel: PropTypes.node.isRequired,
  typeCheck: PropTypes.string,
  warningMessage: PropTypes.node,
  adminCheck: PropTypes.bool,
  adminCheckFailureMessage: PropTypes.node,
  notifyError: PropTypes.bool,
  icon: PropTypes.elementType
}
